import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from "react-router-dom";

import Grid from '@mui/material/Grid2';

import { 
  ModelBase,
} from "@models";
import { 
  Deity, 
  GameClass,
  Alignment
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import LoadingBox from "@components/input/LoadingBox";
import ModelBaseInput from "@dndComponents/ModelBaseInput";
import SuggestionStringBox from "@dndComponents/select/SuggestionStringBox";
import SelectAlignmentBox from "@dndComponents/select/SelectAlignmentBox";
import SelectCreatureBox from "@dndComponents/select/SelectCreatureBox";
import StringBox from '@components/input/strings/StringBox';
import ButtonBox from '@components/input/buttons/ButtonBox';
import StringsBox from '@components/input/strings/StringsBox';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function DeityEdit() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [obj, setObj] = useState<Deity>(api.applyWorkingOn(new Deity()));
  const [cleric_classes, setClericClasses] = useState<string[]>([]);
  const [change_time, setChangeTime] = useState(0);
  const [parse_string, setParseString] = useState("");
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.getObjectsWithVersions({ data_type: "game_class", filter: { name: "Cleric" }});
    setClericClasses((res as GameClass[]).map(o => o._id));
    if (id && id !== "") {
      const res2 = await api.getFullObject<Deity>("deity", id);
      if (res2) {
        setObj(res2.clone());
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
    setFetched(true);
  }, []);

  useEffect(() => {
    if(!fetched) {
      load();
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateObj = (updater: (o: Deity) => void) => {
    // debugger;
    const new_obj = obj.clone(); // clone, don't mutate
    updater(new_obj);
    setObj(new_obj);
    setChangeTime(common_util.get_time_number());
    // ModelBaseInput will pick this up via change_time + obj prop
  };

  const parse = () => {
    const new_obj = obj.clone(); // clone, don't mutate
    // debugger;
    const pieces = parse_string.split(" ");
    // The alignment is key.
    let name = "";
    let comma_found = false;
    let title = "";
    let alignment: Alignment | undefined;
    let domains: string[] = [];
    let symbol = "";
    let titles: string[] = [];
    for (let i = 0; i < pieces.length; ++i) {
      const piece = pieces[i];
      if (!alignment) {
        if (piece.length < 3) {
          if (piece === "N") {
            alignment = {
              order: "Neutral",
              moral: "Neutral"
            };
            comma_found = true;
            continue;
          } else if (piece.length === 2) {
            const order_char = piece[0];
            const moral_char = piece[1];
            if (["L","C","N"].includes(order_char) && ["G","E","N"].includes(moral_char)) {
              alignment = { order: "Any", moral: "Any" };
              switch (order_char) { 
                case "L": alignment.order = "Lawful"; break;
                case "C": alignment.order = "Chaotic"; break;
                case "N": alignment.order = "Neutral"; break;
              }
              switch (moral_char) { 
                case "G": alignment.moral = "Good"; break;
                case "E": alignment.moral = "Evil"; break;
                case "N": alignment.moral = "Neutral"; break;
              }
              comma_found = true;
              continue;
            }
          }
        }
        if (comma_found) {
          if (piece.endsWith(",")) {
            title += piece.substring(0, piece.length - 1);
            titles.push(title.trim());
            title = "";
          } else {
            title += piece + " ";
          }
        } else if (piece.endsWith(",")) {
          name += piece.substring(0, piece.length - 1);
          comma_found = true;
        } else {
          name += piece + " ";
        }
      } else {
        if (comma_found) {
          // After we've found the alignment, comma_found means it's part of the Divine Domains
          if (piece.endsWith(",")) {
            domains.push(piece.substring(0, piece.length - 1));
          } else {
            domains.push(piece);
            comma_found = false;
          }
        } else {
          symbol += piece + " ";
        }
      }
    }
    new_obj.name = name.trim();
    if (title.length > 0) {
      titles.push(title.trim());
    }
    new_obj.titles = titles;
    if (alignment) {
      new_obj.alignment = alignment;
    }
    new_obj.divine_domains = domains;
    new_obj.symbol = symbol;
    setObj(new_obj);
    setChangeTime(common_util.get_time_number());
    setReloading(true);
  }
  
  const height = window.innerHeight - 64;
  if (!fetched || loading || reloading) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else {
    return (
      <ModelBaseInput
        obj={obj}
        disabled={disabled}
        change_time={change_time}
        onChange={(changed: ModelBase) => {
          if (changed instanceof Deity) {
            const new_obj = obj;
            new_obj.copy(changed);
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }
        }}>
        <Grid container spacing={0} direction="row">
          <Grid size={9}>
            <StringBox
              name="Parse String"
              value={parse_string}
              disabled={disabled}
              onBlur={setParseString}
            />
          </Grid>
          <Grid size={3}>
            <ButtonBox
              name="Parse"
              disabled={disabled}
              onClick={parse}
            />
          </Grid>
          <Grid size={12}>
            <SelectAlignmentBox
              name="Alignment"
              value={obj.alignment}
              disabled={disabled}
              onChange={(changed: Alignment) => updateObj(o => { o.alignment = changed; })}
            />
          </Grid>
          <Grid size={12}>
            <StringsBox 
              name="Titles"
              values={obj.titles}
              disabled={disabled}
              onChange={(changed: string[]) => updateObj(o => { o.titles = changed; })}
            />
          </Grid>
          <Grid size={12}>
            <StringsBox 
              name="Derogatory Titles"
              values={obj.derogatory_titles}
              disabled={disabled}
              onChange={(changed: string[]) => updateObj(o => { o.derogatory_titles = changed; })}
            />
          </Grid>
          <Grid size={6}>
            <StringBox 
              name="Symbol"
              value={obj.symbol}
              disabled={disabled}
              onBlur={(changed: string) => updateObj(o => { o.symbol = changed; })}
            />
          </Grid>
          <Grid size={6}>
            <SuggestionStringBox 
              name="Pantheons"
              field="pantheons"
              values={obj.pantheons}
              disabled={disabled}
              multiple
              onChange={(changed: string[]) => updateObj(o => { o.pantheons = changed; })}
            />
          </Grid>
          <Grid size={12}>
            <SuggestionStringBox 
              name="Settings"
              field="settings"
              values={obj.settings}
              multiple
              disabled={disabled}
              onChange={(changed: string[]) => updateObj(o => { o.settings = changed; })}
            />
          </Grid>
          <Grid size={12}>
            <SuggestionStringBox 
              name="Native Planes"
              field="native_planes"
              values={obj.native_planes}
              multiple
              disabled={disabled}
              onChange={(changed: string[]) => updateObj(o => { o.native_planes = changed; })}
            />
          </Grid>
          <Grid size={12}>
            <SuggestionStringBox
              name="Divine Domains"
              field="divine_domains"
              values={obj.divine_domains}
              multiple
              disabled={disabled}
              onChange={(changed: string[]) => updateObj(o => { o.divine_domains = changed; })}
            />
          </Grid>
          <Grid size={12}>
            <SelectCreatureBox
              name="Associated Creatures"
              multiple
              values={obj.associated_creatures}
              disabled={disabled}
              onChange={(changed: string[]) => updateObj(o => { o.associated_creatures = changed; })}
            />
          </Grid>
        </Grid>
      </ModelBaseInput>
    );
  }
}